import { Dropdown } from "antd";
import { ChevronsUpDown, Sparkles } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { resetCreateNewPartyStatus } from "src/store/slices/HomeSlice";
import { useClerk, useUser } from "@clerk/clerk-react";
import { setShowAccountModal, setAccountModalTab } from "src/store/slices/modalSlice";
import useValidatePageAccess from "src/hooks/useValidatePageAccess";
import { logoutUser, trackAnalytics } from "src/pages/helpers";
import SubscribeCTA from "src/components/SubscribeCTA";
import { TIER_DESCRIPTORS } from "src/domain/plan/descriptors";
import {
  EVENT_NAMES,
  EVENT_PARAMETERS,
  BUTTON_ROLES,
} from "src/utils/EventConstants";

const AVATAR_COLORS = ["#EAE5D2", "#D2EAD9", "#EADCD2", "#D2DBEA", "#EAD4D2", "#DCD2EA"];

const getInitials = (user) => {
  const first = user?.firstName?.[0] ?? "";
  const last = user?.lastName?.[0] ?? "";
  return (first + last).toUpperCase() || "?";
};

const getAvatarColor = (user) => {
  const name = (user?.firstName ?? "") + (user?.lastName ?? "");
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const InitialsAvatar = ({ user, size = 36 }) => (
  <div
    className="flex items-center justify-center rounded-full flex-shrink-0"
    style={{ width: size, height: size, backgroundColor: getAvatarColor(user) }}
  >
    <span
      className="font-['DM_Sans'] font-medium text-[14px] leading-[18px] text-center text-[#141414]"
    >
      {getInitials(user)}
    </span>
  </div>
);

const UserProfile = () => {
  useValidatePageAccess();

  const dispatch = useDispatch();

  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const sidenavCollapsed = useSelector((state) => state.homeslice.sidenavCollapsed);
  const documentPanelOpen = useSelector((state) => state.messageSlice.documentPanelOpen);
  const effectiveCollapsed = sidenavCollapsed || documentPanelOpen;
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const email = useSelector((state) => state.homeslice.email);

  const planLabel = TIER_DESCRIPTORS[userMetadata?.subscription_type]?.name ?? "Free";

  if (!isSignedIn) return null;

  return (
    <div className="z-[51]">
      <div className="flex flex-col py-[4px] gap-[20px]">
        <Dropdown
          overlayStyle={{ zIndex: 100 }}
          dropdownRender={() => (
            <div
              data-testid="user-profile-dropdown"
              className="rounded-md overflow-hidden shadow-md min-w-[224px] max-w-[300px] border-t border-[#E5E5E5] bg-white"
            >
              {/* Header: avatar + name + email */}
              <div className="w-full flex flex-col bg-white p-4 gap-[12px]">
                <div className="flex items-center gap-[10px]">
                  <InitialsAvatar user={user} size={32} />
                  <div className="flex flex-col min-w-0">
                    <p className="font-['DM_Sans'] font-medium text-sm leading-tight text-[#141414] truncate">
                      {user.fullName}
                    </p>
                    <p className="font-['DM_Sans'] font-normal text-xs leading-tight text-[#7A7A7A] truncate">
                      {email}
                    </p>
                  </div>
                </div>

                {/* Upgrade plan button */}
                <SubscribeCTA
                  entryPoint="settings_dropdown"
                  className="flex items-center justify-center gap-[6px] w-full py-[8px] px-[16px] rounded-[8px] border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5] cursor-pointer text-[14px] font-[500] font-sans text-[#0A0A0A] transition-colors"
                  label={
                    <>
                      <Sparkles size={14} className="text-[#0A0A0A]" />
                      Upgrade plan
                    </>
                  }
                />
              </div>

              {/* Divider + menu items */}
              <div className="border-t border-[#E5E5E5]" />
              <div className="flex flex-col bg-white py-[8px]">
                <button
                  data-testid="profile-account"
                  type="button"
                  onClick={() => {
                    trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.ACCOUNT,
                    });
                    dispatch(resetCreateNewPartyStatus());
                    dispatch(setAccountModalTab(null));
                    dispatch(setShowAccountModal(true));
                  }}
                  className="w-full text-left px-4 py-[10px] font-['DM_Sans'] font-normal text-sm leading-tight text-[#141414] hover:bg-[#F5F5F5] cursor-pointer bg-transparent border-none"
                >
                  Account
                </button>
                <button
                  data-testid="profile-billing"
                  type="button"
                  onClick={() => {
                    trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.BILLING,
                    });
                    dispatch(setAccountModalTab("billing"));
                    dispatch(setShowAccountModal(true));
                  }}
                  className="w-full text-left px-4 py-[10px] font-['DM_Sans'] font-normal text-sm leading-tight text-[#141414] hover:bg-[#F5F5F5] cursor-pointer bg-transparent border-none"
                >
                  Billing
                </button>
                <button
                  data-testid="logout-btn"
                  type="button"
                  onClick={() => {
                    trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.SIGN_OUT,
                    });
                    logoutUser();
                    signOut({ redirectUrl: process.env.REACT_APP_POST_SIGNOUT_URL || "https://inhouse.ai" });
                  }}
                  className="w-full text-left px-4 py-[10px] font-['DM_Sans'] font-normal text-sm leading-tight text-[#141414] hover:bg-[#F5F5F5] cursor-pointer bg-transparent border-none"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
          trigger={["click"]}
          className="hover:cursor-pointer hover:text-[#5e59ff]"
        >
          <div className="flex w-full items-center gap-[10px]">
            <InitialsAvatar user={user} />
            {!effectiveCollapsed && user?.firstName && (
              <div className="flex flex-1 items-center justify-between min-w-0">
                <div className="flex flex-col min-w-0 leading-tight">
                  <p className="font-['DM_Sans'] font-medium text-[14px] leading-[150%] text-[#0F0F0F] truncate">
                    {user.firstName}
                  </p>
                  <p
                    data-testid="sidenav-plan-badge"
                    className="font-['DM_Sans'] font-normal text-[12px] leading-[150%] text-[#737373] truncate"
                  >
                    {planLabel}
                  </p>
                </div>
                <ChevronsUpDown size={16} className="text-[#737373] flex-shrink-0" />
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default UserProfile;
